import { BrowserLocationEvent } from 'pf-frontend-common/src/module/browser/location.event';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { JsonApiModelInterface } from 'pf-frontend-common/src/module/json-api/model.interface';
import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';
import { CtaFilterPropertyType } from 'pf-frontend-common/src/module/stats/types';
import { WindowLocationServiceInterface } from 'pf-frontend-common/src/service/window-location/service.interface';

import { AgentModel } from 'common/data/agent/model';
import { BrokerModel } from 'common/data/broker/model';
import { PropertyStoreEvent } from 'common/data/property/store.event';
import { PropertyStoreSearchInputInterface } from 'common/data/property/store/search/input.interface';
import { PropertyStoreSearchOutputInterface } from 'common/data/property/store/search/output.interface';
import { configGetSettings } from 'common/helper/config/get-settings';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStoreEvent } from 'common/module/data/store.event';
import { DataStoreDispatchEventInterface } from 'common/module/data/store/dispatch-event.interface';
import { DropdownViewStore } from 'common/module/dropdown/view-store';
import { DropdownViewStoreEvent } from 'common/module/dropdown/view-store.event';
import { LoaderViewStore } from 'common/module/loader/view-store';
import { PaginationViewStore } from 'common/module/pagination/view-store';
import { PaginationViewStoreEvent } from 'common/module/pagination/view-store.event';
import { PropertyListViewStore } from 'common/module/property/list/view-store';
import { PropertyParamStore } from 'common/module/property/param-store';
import { PropertyParamStoreStateInterface } from 'common/module/property/param-store/state.interface';
import { PropertyQueryParamsAdapter } from 'common/module/property/query-params-adapter';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { PropertyStoreServiceInterface } from 'common/service/property-store/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { domQuerySelector } from 'common/typings/dom-query-selector';
import { ClientPageTabViewEvent } from 'desktop/module/client/page/tab/view.event';
import { ClientPageTabViewInterface } from 'desktop/module/client/page/tab/view.interface';
import { PropertyTabCtaAdapter } from 'desktop/module/property/tab/cta-adapter';
import { PropertyTabHtmlElementInterface } from 'desktop/module/property/tab/html-element.interface';
import { PropertyTabViewOptionsInterface } from 'desktop/module/property/tab/view-options.interface';

export class PropertyTabView implements ClientPageTabViewInterface {
  /**
   * Main view element
   */
  private element: HTMLElement;

  /**
   * Model to whom the properties belong to
   */
  private model: JsonApiModelInterface;

  /**
   * Whether tab is activated
   */
  private activated: boolean = false;

  /**
   * CSS selectors
   */
  private cssSelector: DataKeyValueStringInterface = {
    propertyList: '[data-qs="properties"]',
    propertyTotal: '[data-qs="properties-total"]',
    pagination: '[data-qs="pagination"]',
    sortDropdown: '[data-qs="sort-dropdown"]',
    categoryDropdown: '[data-qs="category-dropdown"]',
    cta: '[data-qs="cta"]',
    loader: '[data-qs="loader"]',
  };

  /**
   * CSS class
   */
  private cssClass: DataKeyValueStringInterface = {
    dropdown: 'dropdown--bordered',
  };

  /**
   * HTML elements
   */
  private htmlElement: PropertyTabHtmlElementInterface;

  /**
   * Constructor
   */
  constructor(
    private eventEmitter: EventEmitterInterface,
    private queryParamsAdapter: PropertyQueryParamsAdapter,
    private windowLocationService: WindowLocationServiceInterface,
    private propertyListViewDynamic: ViewDynamicInterface<PropertyListViewStore>,
    private paginationViewDynamic: ViewDynamicInterface<PaginationViewStore>,
    private sortDropdownViewDynamic: ViewDynamicInterface<DropdownViewStore>,
    private categoryDropdownViewDynamic: ViewDynamicInterface<DropdownViewStore>,
    private loaderViewDynamic: ViewDynamicInterface<LoaderViewStore>,
    private propertyStoreService: PropertyStoreServiceInterface,
    private propertyParamStore: PropertyParamStore,
    private statsService: StatsServiceInterface,
    private domQuerySelectorHelper: domQuerySelector
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: PropertyTabViewOptionsInterface): void {
    // Keep element
    this.element = options.element;

    // Update HTML elements
    this.htmlElement = {
      dropdownCategory: this.domQuerySelectorHelper(this.element, this.cssSelector.categoryDropdown),
      dropdownSort: this.domQuerySelectorHelper(this.element, this.cssSelector.sortDropdown),
      propertyTotal: this.domQuerySelectorHelper(this.element, this.cssSelector.propertyTotal),
    };

    // Keep model
    this.model = options.model;

    // Initialize param store
    this.initParamStore(options);

    // Initialize subviews
    this.initPagination(options);
    this.initPropertyList(options);
    this.initDropdownSort(options);
    this.initDropdownCategory(options);
    this.initLoader();

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    // Render subviews
    this.sortDropdownViewDynamic.render();
    this.categoryDropdownViewDynamic.render();
    this.propertyListViewDynamic.render();
    this.paginationViewDynamic.render();
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * Activates/deactivates tab
   *
   * @param activated - whether tab activated
   */
  public setActivated(activated: boolean): void {
    this.activated = activated;

    // Enable/disable list
    this.propertyListViewDynamic.getViewStore().updateDisabled(!activated);
  }

  /**
   * Update property list
   */
  public updatePropertyList(searchOutput: PropertyStoreSearchOutputInterface): void {
    // Update property total
    if (this.htmlElement.propertyTotal) {
      this.htmlElement.propertyTotal.innerHTML = String(searchOutput.pagination.totalCount);
    }

    // Update pagination
    this.paginationViewDynamic
      .getViewStore()
      .updatePagination(searchOutput.pagination.page, searchOutput.pagination.totalCount);

    const properties = searchOutput.properties.map((propertyModel) => {
      // Force property to use listing_level as listing_level_label value (property tab does not support CTS/SmartAd)
      propertyModel.listing_level_label = propertyModel.listing_level;

      return propertyModel;
    });

    // Update list properties
    this.propertyListViewDynamic.getViewStore().updateProperties(properties, this.getStatsContext());
  }

  /**
   * Get stats context local to this view
   */
  private getStatsContext(): StatsContextLocalInterface {
    const state = this.paginationViewDynamic.getViewStore().getState();

    return {
      pagination: {
        pageCurrent: state.currentPage,
        itemPerPage: state.limit,
        itemTotal: state.total,
      },
    };
  }

  /**
   * Attach observers
   */
  private observers(): void {
    // Property param
    this.propertyParamStore.getEventEmitter().addListener(DataStoreEvent.updateState, this.onUpdateStateParamStore);

    // Property store
    this.propertyStoreService
      .getEventEmitter()
      .addListener(PropertyStoreEvent.searchSuccess, this.onSearchSuccessPropertyStore);

    this.propertyStoreService
      .getEventEmitter()
      .addListener(PropertyStoreEvent.searchStart, this.onSearchStartPropertyStore);

    // Window location
    this.windowLocationService.getEventEmitter().addListener(BrowserLocationEvent.pop, this.onPopBrowserLocation);

    // Pagination
    this.paginationViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(PaginationViewStoreEvent.clickPage, this.onClickPagePagination);
  }

  /**
   * Inits param store
   */
  private initParamStore(options: PropertyTabViewOptionsInterface): void {
    // Property param store
    this.propertyParamStore.updateParams({
      entity: this.model.jsonApiType,
      entityId: parseInt(this.model.id, 10),
      sort: (options.form.orderBy && options.form.orderBy[0]) || '',
      filterCategoryId: options.form.hasOwnProperty('categoryId') ? options.form.categoryId : '',
      pageNumber: options.payload.meta.page,
      pageLimit: options.payload.meta.per_page || options.form.itemsPerPage,
    });
  }

  /**
   * Inits property list
   */
  private initPropertyList(options: PropertyTabViewOptionsInterface): void {
    this.propertyListViewDynamic.initialize({
      element: this.domQuerySelectorHelper(this.element, this.cssSelector.propertyList),
    });

    this.propertyListViewDynamic.getViewStore().initialize({
      isTargetBlank: !!configGetSettings().whitelabel,
    });

    // Update search result using property store search output
    this.updatePropertyList(this.propertyStoreService.getSearchOutput(options.payload));
  }

  /**
   * Init pagination
   */
  private initPagination(options: PropertyTabViewOptionsInterface): void {
    this.paginationViewDynamic.initialize({
      element: this.domQuerySelectorHelper(this.element, this.cssSelector.pagination),
    });

    this.paginationViewDynamic.getViewStore().initialize({
      currentPage: options.payload.meta.page,
      limit: options.payload.meta.per_page || options.form.itemsPerPage,
      total: options.payload.meta.total_count,
      pageCount: options.payload.meta.page_count,
      nextLinkTitle: i18nTranslate('next'),
      prevLinkTitle: i18nTranslate('prev'),
    });
  }

  /**
   * Inits sort dropdown
   */
  private initDropdownSort(options: PropertyTabViewOptionsInterface): void {
    this.sortDropdownViewDynamic.initialize({
      element: this.domQuerySelectorHelper(this.element, this.cssSelector.sortDropdown),
    });

    this.sortDropdownViewDynamic.getViewStore().initialize({
      value: this.propertyParamStore.getState().sort,
      options: options.form.orderByOptions.map((option) => ({
        value: option.id,
        text: option.text,
      })),
      class: this.cssClass.dropdown,
    });

    this.sortDropdownViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(DropdownViewStoreEvent.clickWindow, this.onClickWindowDropdownSort);

    this.sortDropdownViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(DropdownViewStoreEvent.change, this.onChangeDropdownSort);
  }

  /**
   * Inits category dropdown
   */
  private initDropdownCategory(options: PropertyTabViewOptionsInterface): void {
    const element = this.domQuerySelectorHelper(this.element, this.cssSelector.categoryDropdown);

    this.categoryDropdownViewDynamic.initialize({
      element,
    });

    this.categoryDropdownViewDynamic.getViewStore().initialize({
      value: this.propertyParamStore.getState().filterCategoryId,
      options: [
        {
          value: '',
          text: i18nTranslate('All'),
        },
        ...options.form.categoryOptions.map((option) => ({
          value: option.id,
          text: option.text,
        })),
      ],
      class: this.cssClass.dropdown,
    });

    this.categoryDropdownViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(DropdownViewStoreEvent.clickWindow, this.onClickWindowDropdownCategory);

    this.categoryDropdownViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(DropdownViewStoreEvent.change, this.onChangeDropdownCategory);
  }

  /**
   * Inits loader
   */
  private initLoader(): void {
    this.loaderViewDynamic.initialize({
      element: this.domQuerySelectorHelper(this.element, this.cssSelector.loader),
    });

    // Center loader if there are no results
    this.loaderViewDynamic.getViewStore().center(!this.paginationViewDynamic.getViewStore().getState().total);
  }

  /**
   * Perform search
   */
  private search(params: PropertyParamStoreStateInterface): void {
    const searchInput: PropertyStoreSearchInputInterface = {
      page: {
        limit: params.pageLimit,
        number: params.pageNumber,
      },
      filter: {
        category_id: params.filterCategoryId,
      },
      sort: params.sort,
    };

    if (params.entity === AgentModel.JSONAPI_TYPE) {
      searchInput.filter.agent_id = String(params.entityId);
    }

    if (params.entity === BrokerModel.JSONAPI_TYPE) {
      searchInput.filter.broker_id = String(params.entityId);
    }

    // Perform search
    this.propertyStoreService.search(searchInput);
  }

  /**
   * Update window location (URL)
   */
  private updateUrl(params: PropertyParamStoreStateInterface): void {
    const queryData = {
      ...this.windowLocationService.getQueryData(),
      properties: this.queryParamsAdapter.toCompact(this.queryParamsAdapter.fromPropertyParamStoreParams(params)),
    };

    if (queryData.properties.page === 1) {
      delete queryData.properties.page;
    }

    this.windowLocationService.replace(this.windowLocationService.getPathname(), {
      hash: this.windowLocationService.getHash(),
      queryData,
    });
  }

  /**
   * A store search request has been made
   */
  private onSearchStartPropertyStore = (): void => {
    // Disable list
    this.propertyListViewDynamic.getViewStore().updateDisabled(true);

    // Enable loader
    this.loaderViewDynamic.getViewStore().enable();

    if (this.activated) {
      this.getEventEmitter().emit(ClientPageTabViewEvent.update);
    }
  };

  /**
   * A store search request has finished
   */
  private onSearchSuccessPropertyStore = (searchOutput: PropertyStoreSearchOutputInterface): void => {
    // Update search result using property store search output
    this.updatePropertyList(searchOutput);

    // Center loader if there is no results
    this.loaderViewDynamic.getViewStore().center(!searchOutput.pagination.totalCount);

    // Disable loader
    this.loaderViewDynamic.getViewStore().disable();

    // Enable list
    this.propertyListViewDynamic.getViewStore().updateDisabled(false);
  };

  /**
   * Sort dropdown view store state updated
   */
  private onChangeDropdownSort = (): void => {
    const selected = <string>this.sortDropdownViewDynamic.getViewStore().getState().value;

    // Log stats
    this.statsService.ctaClick({
      cta: {
        sort: {
          property: new PropertyTabCtaAdapter().transformSortOption(selected),
        },
      },
    });

    // Update params
    this.propertyParamStore.updateParams({
      sort: selected,
      pageNumber: 1,
    });
  };

  /**
   * Category dropdown view store state updated
   */
  private onChangeDropdownCategory = (): void => {
    const selected = <CtaFilterPropertyType>this.categoryDropdownViewDynamic.getViewStore().getState().value;

    // Log stats
    this.statsService.ctaClick({
      cta: {
        filter: {
          property: selected || '0',
        },
      },
    });

    // Update params
    this.propertyParamStore.updateParams({
      filterCategoryId: selected,
      pageNumber: 1,
    });
  };

  /**
   * Pagination view store state updated
   */
  private onClickPagePagination = (): void => {
    // Update params
    this.propertyParamStore.updateParams({
      pageNumber: this.paginationViewDynamic.getViewStore().getState().currentPage,
    });
  };

  /**
   * Updated state of param store
   */
  private onUpdateStateParamStore = (storeDispatchEvent: DataStoreDispatchEventInterface): void => {
    // Updated params
    if (storeDispatchEvent.action === this.propertyParamStore.updateParams) {
      const params = this.propertyParamStore.getState();

      // Update window location
      this.updateUrl(params);

      // Perform a search
      this.search(params);
    }
  };

  /**
   * Sync the store to the location when the URL updates
   */
  private onPopBrowserLocation = () => {
    let shouldUpdate = false;

    const params = this.propertyParamStore.getState();

    // Map query data to state params
    const qParams = this.queryParamsAdapter.toPropertyParamStoreParams(
      this.windowLocationService.getQueryData().properties || {}
    );

    Object.keys(params).forEach((key: keyof PropertyParamStoreStateInterface) => {
      if (qParams[key] && qParams[key] !== params[key]) {
        shouldUpdate = true;
      }
    });

    if (!shouldUpdate) {
      return;
    }

    // Update params
    this.propertyParamStore.updateParams(qParams);
  };

  /**
   * Clicked the window from dropdown category
   */
  private onClickWindowDropdownCategory = (e: Event) => {
    if (this.htmlElement.dropdownCategory.contains(e.target as HTMLElement)) {
      return;
    }

    this.categoryDropdownViewDynamic.getViewStore().close();
  };

  /**
   * Clicked the window from dropdown sort
   */
  private onClickWindowDropdownSort = (e: Event) => {
    if (this.htmlElement.dropdownSort.contains(e.target as HTMLElement)) {
      return;
    }

    this.sortDropdownViewDynamic.getViewStore().close();
  };
}
